import { useState } from "react";
import { Link } from "react-router-dom";
import type { Chapter } from "@/utils/comics";

interface ChapterListProps {
  comicId: string;
  chapters: Chapter[];
}

const PAGE_SIZE = 24;

export default function ChapterList({ comicId, chapters }: ChapterListProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(chapters.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const current = chapters.slice(start, start + PAGE_SIZE);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-list-check-2 text-lg" />
          </span>
          <h2 className="text-lg font-bold text-foreground-950">章节目录</h2>
          <span className="text-xs text-foreground-500">共 {chapters.length} 话</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <span className="text-foreground-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {current.map((ch) => (
          <Link
            key={ch.id}
            to={`/read/${comicId}/${ch.id}`}
            className="group flex items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50"
          >
            <span className="line-clamp-1 text-sm text-foreground-800 group-hover:text-primary-700">
              {ch.title}
            </span>
            <i className="ri-arrow-right-s-line shrink-0 text-foreground-400 group-hover:text-primary-600" />
          </Link>
        ))}
      </div>
    </section>
  );
}